import type { ActivityRow, ProgressSegment } from "../types/npi-dashboard.types";
import { findProjectActivity, getSubgroupActivities, getProjectActivities, getVisibleActivities } from "./activity.utils";
import { NpiProject } from "../types/npi-project.types";

export const normalizeText = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const calculatePercentage = (
  value: number | undefined,
  total: number,
): number => (total > 0 ? ((value ?? 0) / total) * 100 : 0);

export const buildActivityRows = (
  projects: NpiProject[],
): ActivityRow[] => {
  const seen = new Set<string>();
  const result: ActivityRow[] = [];
  for (const project of projects) {
    for (const group of project.groups ?? []) {
      for (const activity of getVisibleActivities(group)) {
        const acronym = activity.acronym ?? "";
        const key = `${group.name}|${acronym}`;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push({ key, phase: group.name, step: acronym, activity: acronym });
      }
      for (const { subgroup, activity } of getSubgroupActivities(group)) {
        const acronym = activity.acronym ?? "";
        const key = `${group.name}|${subgroup.name}|${acronym}`;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push({ key, phase: group.name, step: subgroup.name, subgroup: subgroup.name, activity: acronym });
      }
    }
  }
  const phaseOrder = Array.from(new Set(result.map((row) => row.phase)));
  return result.sort(
    (a, b) => phaseOrder.indexOf(a.phase) - phaseOrder.indexOf(b.phase),
  );
};

export const filterProjects = (
  projects: NpiProject[],
  searchTerm: string,
  matchingRows: ActivityRow[],
): NpiProject[] => {
  if (!searchTerm) return projects;
  return projects.filter(
    (project) =>
      [project.client, project.projectName].some((value) =>
        normalizeText(String(value ?? "")).includes(searchTerm),
      ) ||
      matchingRows.some((row) =>
        findProjectActivity(project, row),
      ),
  );
};

export const calculateProgressSegments = (
  project: NpiProject,
): ProgressSegment[] => {
  const metrics = getProjectProgressMetrics(project);

  return [
    {
      color: "#22C55E",
      width: calculatePercentage(
        metrics.completedActivities,
        metrics.totalActivities,
      ),
    },
    {
      color: "#3B82F6",
      width: calculatePercentage(
        metrics.inProgressActivities,
        metrics.totalActivities,
      ),
    },
    {
      color: "#F87171",
      width: calculatePercentage(
        Math.max(0, metrics.delayedActivities),
        metrics.totalActivities,
      ),
    },
    {
      color: "#F59E0B",
      width: calculatePercentage(0, metrics.totalActivities),
    },
  ];
};

export const getProjectProgressMetrics = (project: NpiProject) => {
  const activities = getProjectActivities(project).filter(
    (activity) => activity.status !== "nao_sera_executada",
  );
  const totalActivities = project.totalActivities || 0;
  const completedActivities =
    project.completedActivities || activities.filter((activity) => activity.status === "concluido").length;
  const inProgressActivities =
    project.inProgressActivities ?? activities.filter((activity) => activity.status === "em_andamento").length;
  const delayedActivities =
    project.delayedActivities ?? activities.filter((activity) => activity.status === "atrasado").length;
  const progress = project.progress || (totalActivities ? Math.round((completedActivities / totalActivities) * 100) : 0);

  return {
    totalActivities,
    completedActivities,
    inProgressActivities,
    delayedActivities,
    progress,
  };
};
